"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Droplets, Recycle, Leaf, CircleAlert as AlertCircle, ArrowRight, TrendingDown, ShieldCheck, ChartBar as BarChart3 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
const impactPillars = [{
  icon: Droplets,
  badgeText: "Resource Efficiency",
  title: "Solvent Procurement Reduction",
  highlight: "Up to 80% Solvent Savings",
  description: "Modern chromatography and sample preparation systems significantly reduce mobile phase solvent consumption.",
  points: ["Lower annual chemical purchasing expenditure", "Decreased high-purity solvent storage footprints", "Less supply chain vulnerability and freight overhead"]
}, {
  icon: Recycle,
  badgeText: "Risk & Disposal",
  title: "Hazardous Waste Stream Minimization",
  highlight: "Substantial Effluent Reduction",
  description: "Minimizing organic solvent consumption directly curbs hazardous chemical effluent and lab incineration volumes.",
  points: ["Fewer chemical waste collection cycles required", "Enhanced analyst safety with lower VOC exposure", "Strict alignment with regional EPA compliance mandates"]
}, {
  icon: Leaf,
  badgeText: "Corporate Governance",
  title: "ESG & Lab Sustainability Targets",
  highlight: "Green Lab Accreditation",
  description: "Support institutional Scope 1-3 carbon reporting and sustainability frameworks across regulated facilities.",
  points: ["Direct contributions to My Green Lab certification standards", "Lower thermal emissions and laboratory HVAC load", "Verifiable metrics for corporate ESG audit readiness"]
}];
export default function SustainabilityImpact() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };
  return <section id="sustainabilityimpact" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="outline" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold">
              <Leaf className="size-3 text-primary" />
              Green Laboratory Operations
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Analytical Precision with Lower Environmental Impact
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Advanced instrumentation architectures help pharmaceutical, life science, and testing laboratories across Ireland and the UK reduce raw chemical consumption, lower hazardous waste streams, and fulfill sustainability goals.
          </p>
        </div>

        {/* 3-Column Pillars Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-80px"
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-10">
          {impactPillars.map((pillar, i) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} variants={itemVariants} data-index={i} className="flex">
                <Card className="flex flex-col justify-between w-full border border-border shadow-xs hover:shadow-md transition-[box-shadow,border-color] duration-300">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-primary">
                        <Icon className="size-5" />
                      </div>
                      <Badge variant="secondary" className="text-xs font-medium">
                        {pillar.badgeText}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl md:text-2xl font-bold font-default text-foreground">
                      {pillar.title}
                    </CardTitle>
                    <div className="inline-flex items-center gap-1.5 text-primary text-xs font-semibold mt-1">
                      <TrendingDown className="size-3.5" />
                      <span>{pillar.highlight}</span>
                    </div>
                    <CardDescription className="text-muted-foreground text-sm leading-relaxed mt-2">
                      {pillar.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <div className="border-t border-border pt-4">
                      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                        Operational Outcomes:
                      </p>
                      <ul className="space-y-2.5">
                        {pillar.points.map((pt, pIdx) => <li key={pIdx} className="flex items-start gap-2 text-sm text-foreground">
                            <ShieldCheck className="size-4 text-primary shrink-0 mt-0.5" />
                            <span className="leading-tight">{pt}</span>
                          </li>)}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Prominent Disclaimer Callout Box */}
        <div className="mb-12">
          <Alert className="border-border bg-secondary/60 text-foreground py-4 px-5 rounded-lg shadow-2xs">
            <div className="flex items-start gap-3">
              <AlertCircle className="size-5 text-primary shrink-0 mt-0.5" />
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <AlertTitle className="text-sm font-semibold text-foreground tracking-tight">
                    Illustrative Examples Only
                  </AlertTitle>
                  <Badge variant="outline" className="text-[10px] uppercase font-bold py-0 h-4 border-muted-foreground/30">
                    Disclaimer
                  </Badge>
                </div>
                <AlertDescription className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                  Any figures, percentage reductions, or projected efficiency gains cited on this page are illustrative examples based on typical industry benchmarks. They are not guaranteed performance commitments. Actual solvent reduction, waste minimization, and cost savings depend directly on your laboratory&apos;s specific analytical methods, sample throughput, column chemistry, runtime parameters, and standard operational protocols.
                </AlertDescription>
              </div>
            </div>
          </Alert>
        </div>

        {/* Bottom CTA Bar */}
        <div className="bg-secondary/40 border border-border rounded-xl p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg font-bold font-default text-foreground flex items-center justify-center sm:justify-start gap-2">
              <BarChart3 className="size-4 text-primary" />
              Evaluate Your Laboratory Efficiency
            </h3>
            <p className="text-sm text-muted-foreground">
              Speak with our technical specialists to review application-specific methods and lifecycle requirements.
            </p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage">
                <span>Request Lab Assessment</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/SolutionsPage">
                <span>Explore Solutions</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}